import { CSSResultGroup, html, LitElement, nothing, svg } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './autoplay-button.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'


export const autoplayButtonVariants = ['icon-only', 'with-progress'] as const
export type autoplayButtonVariantTypes = typeof autoplayButtonVariants

initializeIconsByRole({
  'cse-autoplay-button.play': { name: 'play', style: 'solid' },
  'cse-autoplay-button.pause': { name: 'pause', style: 'solid' },
})

// The ring sits on the button's own 42px circle, not a larger halo around it.
const RING_RADIUS = 19
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

@customElement('cse-autoplay-button')
export class CSEAutoplayButton extends LitElement {
  static override styles: CSSResultGroup = [fontStyles, hostStyle, style]

  @property({ type: String })
  variant: autoplayButtonVariantTypes[number] = 'icon-only'

  @property({ type: Boolean, reflect: true })
  paused = false

  /** Loop length of the autoplaying content, used to drive the `with-progress` ring. */
  @property({ type: Number, attribute: 'duration-ms' })
  durationMs = 0

  @state()
  private progress = 0

  private animationFrame: number | undefined = undefined
  private loopStart = 0

  override connectedCallback() {
    super.connectedCallback()
    if (!this.paused) {
      this.startProgressLoop()
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    this.stopProgressLoop()
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)

    const restartRelevant = changedProperties.has('paused')
      || changedProperties.has('durationMs')
      || changedProperties.has('variant')

    if (!restartRelevant) {
      return
    }

    if (this.paused) {
      this.stopProgressLoop()
    } else {
      this.startProgressLoop()
    }
  }

  // Drives the ring off a self-contained timer rather than the actual media's
  // currentTime, since this element doesn't own the video/carousel it controls.
  private startProgressLoop() {
    if (this.variant !== 'with-progress' || this.durationMs <= 0) {
      return
    }

    this.stopProgressLoop()
    this.loopStart = performance.now() - (this.progress * this.durationMs)

    const tick = (now: number) => {
      const elapsed = (now - this.loopStart) % this.durationMs
      this.progress = elapsed / this.durationMs
      this.animationFrame = requestAnimationFrame(tick)
    }

    this.animationFrame = requestAnimationFrame(tick)
  }

  private stopProgressLoop() {
    if (this.animationFrame !== undefined) {
      cancelAnimationFrame(this.animationFrame)
      this.animationFrame = undefined
    }
  }

  private onClick = () => {
    this.paused = !this.paused
    this.dispatchEvent(new AutoplayToggleEvent(this, this.paused))
  }

  private get dashOffset(): number {
    return RING_CIRCUMFERENCE * (1 - this.progress)
  }

  // The ring itself is always shown as the button's own border; only the
  // colored progress arc is specific to the `with-progress` variant.
  private renderRing() {
    const showsProgress = this.variant === 'with-progress'

    return html`
      <svg class="progress-ring" width="42" height="42" viewBox="0 0 42 42" aria-hidden="true">
        <circle class="ring-background" cx="21" cy="21" r="${RING_RADIUS}" fill="none" />
        ${showsProgress ? svg`
          <circle
            class="ring-foreground"
            cx="21" cy="21" r="${RING_RADIUS}"
            fill="none"
            stroke-linecap="round"
            style="stroke-dasharray: ${RING_CIRCUMFERENCE}; stroke-dashoffset: ${this.dashOffset};"
          />
        ` : nothing}
      </svg>
    `
  }

  override render() {
    return html`
      <button
        type="button"
        class="${classMap({
          'cse-autoplay-button': true,
          'font-body': true,
          [`variant-${this.variant}`]: true,
        })}"
        aria-pressed="${!this.paused}"
        aria-label="${this.paused
          ? t('play_label', { ns: 'cse-autoplay-button', default: 'Play autoplay content' })
          : t('pause_label', { ns: 'cse-autoplay-button', default: 'Pause autoplay content' })}"
        @click="${this.onClick}"
      >
        ${this.renderRing()}
        <cse-icon
          class="icon"
          size="sm"
          icon-role="${this.paused ? 'cse-autoplay-button.play' : 'cse-autoplay-button.pause'}"
        ></cse-icon>
      </button>
    `
  }
}

export class AutoplayToggleEvent extends Event {
  static type = 'autoplay-toggle' as const

  autoplayButton: CSEAutoplayButton
  paused: boolean

  constructor(autoplayButton: CSEAutoplayButton, paused: boolean) {
    super(AutoplayToggleEvent.type, { bubbles: true, composed: true })
    this.autoplayButton = autoplayButton
    this.paused = paused
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-autoplay-button': CSEAutoplayButton
  }
}
